<template>
    <p-modal
        size="lg"
        name="exportPriceListModal"
        :label="exportPriceListLabel"
        ref="exportPriceListModal"
        @show="initialize"
    >
        <template v-slot:default>
            <p-form ref="priceListForm">
                <p-advanced-select
                    v-if="!termId"
                    v-model="selectedTermId"
                    label="Term"
                    data-type="terms"
                    rules="required"
                ></p-advanced-select>
                <p-checkbox
                    label="Include Course Information"
                    class="mt-3"
                    v-model="priceListParameters.includeCourseInfo"
                />
                <p-checkbox
                    label="Include Publisher"
                    v-model="priceListParameters.includePublisher"
                />
                <UserReportTemplate class="mt-3"
                    :user-report-template-options="userReportTemplateOptions"
                    v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                    :selected-user-report-template="selectedUserReportTemplate"
                    v-model:template-name="templateName"
                    @selected-object="applyUserReportTemplate($event)" />
            </p-form>
        </template>
        <template v-slot:modal-footer>
                <b-row>
                    <b-col>
                        <p-button variant="outline-primary"
                                  :is-busy="isBusy"
                                  @click="exportReport(reportType)">Export</p-button>
                    </b-col>
                </b-row>
        </template>
    </p-modal>        
</template>

<script>


import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import { getExportMessage } from '@/components/Common/ExportMessage.js';
import { useSelectListsStore } from '@/stores/selectLists.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    props: {
        termId: {
            type: Number,
            required: false
        },
    },
    data() {
        return {
            selectedTermId: null,
            isBusy : false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Price List',
            reportType: 'pdf',
            priceListParameters: {
                termId: null,
                includeCourseInfo: false,
                includePublisher: false
            },
        };
    },
     components: {
        UserReportTemplate
    },
    computed: {
        // ReportTemplates works against `reportParameters`; this report's parameters object is
        // `priceListParameters`. Returning it here means the mixin saves and restores the same
        // object the inputs are bound to.
        reportParameters() {
            return this.priceListParameters;
        },
        exportPriceListLabel() {
            return `Export Price List (${this.reportType.toUpperCase()})`;
        }
    },
    methods: {
        async exportReport(reportType){
            if (!(await this.$refs.priceListForm.validate())) {
                return;
            }

            let termId = this.termId || this.selectedTermId
            // Sent through params below rather than the query string, so it isn't duplicated now
            // that it is part of the saved parameters.
            this.priceListParameters.termId = termId;

            let apiUrl = `/terms/exportPriceList${reportType === 'pdf' ? '' : 'csv'}`;
            let fileName = `Price List${reportType === 'pdf' ? '' : '-' + useSelectListsStore().getTermById(termId)?.text}`;

            this.isBusy = true;

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            axios
                .get(apiUrl, {
                    params: this.priceListParameters,
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        reportType === 'pdf' ? fileName : fileName + '.csv',
                        reportType === 'pdf' ? 'application/pdf' : 'application/csv');
                    // The interceptor raises the api's own message, so only claim success without one.
                    if (!getExportMessage(result)) {
                        this.$appSuccess(
                            `Price list report generated successfully.`
                        )
                    }


                    this.$refs.exportPriceListModal.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        initialize(){
            this.selectedTermId = null;
            this.priceListParameters = {
                termId: null,
                includeCourseInfo: false,
                includePublisher: false
            }
        },
        async show(reportType = 'pdf') {
            this.reportType = reportType;
            await this.loadUserReportTemplates();
            this.$refs.exportPriceListModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The term is the only saved reference —
        // the two include flags are literal values.
        async validateReportParameters(savedParameters) {
            if (!savedParameters.termId) {
                return true;
            }

            const terms = await selectListOptionsDataContext.getStoreDropdownData('terms');

            return terms.some(term => term.value == savedParameters.termId);
        },
        // ReportTemplates hook: the Term dropdown is bound to `selectedTermId`, not to the
        // parameters object, so the restored term has to be pushed back into it or the dropdown
        // stays empty and exportReport overwrites the restored value with null.
        onUserReportTemplateApplied() {
            this.selectedTermId = this.priceListParameters.termId;
        }
    }
};

</script>